import { Component, inject, Input, SimpleChanges, OnChanges, AfterViewInit, ViewChild, ElementRef, NgZone, ApplicationRef } from '@angular/core';
import { Message, Status, StatusValues } from '../models/message';
import { CommonModule } from '@angular/common';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MediaPreviewComponent } from './media-preview';
import { ChatStateService } from '../services/chat-state.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-message-history',
  standalone: true,
  imports: [CommonModule, MediaPreviewComponent, MatIconModule, MatButtonModule, MatMenuModule],
  templateUrl: '../templates/message-history.component.html',
  styleUrls: ['../templates/message-history.scss'],
})
export class MessageHistory implements OnChanges, AfterViewInit {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  @Input() messages: Record<string, Message[]> = {};
  @Input() statuses: Record<string, Status[]> = {};

  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private ngZone = inject(NgZone);
  private appRef = inject(ApplicationRef);
  public chatState = inject(ChatStateService);

  constructor() {
    this.matIconRegistry.addSvgIcon(
      'messageFailed',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/message_failed.svg')
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messages']) {
      const hasMedia = Object.values(this.messages).some(msgs => msgs.some(m => m.media?.length));
      if (!hasMedia) this.scrollToBottom();
    }
  }

  ngAfterViewInit() {
    this.scrollToBottom();
  }

  onMediaLoaded() {
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  scrollToBottom() {
    if (!this.scrollContainer) {
      return;
    }
    const el = this.scrollContainer.nativeElement;
    this.ngZone.onStable.pipe().subscribe(() => {
      setTimeout(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }, 50);
    });
  }

  getMessageDates(): string[] {
    return Object.keys(this.messages).filter(date => !isNaN(new Date(date).getTime()));
  }

  getMessagesForDate(date: string): Message[] {
    return this.messages[date] || [];
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatTime(timestamp: Date | string): string {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  getNameInitials(name: string): string | null {
    if (!name) return null;
    const initials = name
      .split(' ')
      .map(part => part[0].toUpperCase())
      .join('');
    return initials || null;
  }

  isErrorStatus(status: StatusValues): boolean {
    return status === StatusValues.FAILED;
  }

  isInboundMessage(message: Message): boolean {
    return (
      message.sender.phoneNumber !== environment.messagingPhone
    );
  }
}
